(() => {
  class CareersJobBoard {
    constructor(element) {
      this.element = element;

      this.jobsContainer = element.querySelector('[data-careers-jobs]');
      this.filtersContainer = element.querySelector('[data-careers-filters]');
      this.paginationContainer = element.querySelector('[data-careers-pagination]');
      this.messageContainer = element.querySelector('[data-careers-message]');
      this.resultCountContainer = element.querySelector('[data-careers-result-count]');

      this.jobs = [];
      this.filteredJobs = [];
      this.allDepartments = [];
      this.currentPage = 1;

      this.config = this.getConfig();

      this.filters = {
        department: '',
        location: '',
        workplaceType: '',
        employmentType: ''
      };

      this.init();
    }

    getConfig() {
      const data = this.element.dataset;

      return {
        apiUrl: data.apiUrl || '',
        departmentsApiUrl: data.departmentsApiUrl || '',
        jobsPerPage: Number.parseInt(data.jobsPerPage, 10) || 10,
        linkTarget: data.linkTarget || '_blank',
        positionSort: data.positionSort || 'ashby',

        departmentStyle: data.departmentStyle || 'h3',
        positionStyle: data.positionStyle || 'h4',
        bodyStyle: data.bodyStyle || 'body',

        showResultCount: data.showResultCount === 'true',

        showDepartmentFilter: data.showDepartmentFilter === 'true',
        showLocationFilter: data.showLocationFilter === 'true',
        showWorkplaceFilter: data.showWorkplaceFilter === 'true',
        showEmploymentFilter: data.showEmploymentFilter === 'true',

        showLocation: data.showLocation === 'true',
        showWorkplace: data.showWorkplace === 'true',
        showEmployment: data.showEmployment === 'true',
        showCompensation: data.showCompensation === 'true',
        showDescription: data.showDescription === 'true',

        descriptionLength: Number.parseInt(data.descriptionLength, 10) || 150,

        labels: {
          allDepartments: data.allDepartmentsLabel || 'All departments',
          allLocations: data.allLocationsLabel || 'All locations',
          allWorkplaces: data.allWorkplacesLabel || 'All workplace types',
          allEmployment: data.allEmploymentLabel || 'All employment types',

          previous: data.previousLabel || 'Previous',
          next: data.nextLabel || 'Next',

          loading: data.loadingMessage || 'Loading open positions...',
          empty: data.emptyMessage || 'There are currently no open positions.',
          noResults: data.noResultsMessage || 'No positions match your selections.',
          emptyDepartment: data.emptyDepartmentMessage || 'There are no open positions in this department right now.',
          error: data.errorMessage || 'We’re unable to load open positions right now.'
        }
      };
    }

    async init() {
      if (!this.config.apiUrl) {
        this.showMessage(this.config.labels.error);
        console.error('Careers job board: jobs API URL is not configured.');
        return;
      }

      this.showMessage(this.config.labels.loading);

      // Jobs and departments are fetched independently: a departments-list
      // failure (or it simply not being configured yet) should not take down
      // the whole board, since job listings alone are still useful.
      const [jobsResult, departmentsResult] = await Promise.all([
        this.fetchJobs(),
        this.fetchDepartments()
      ]);

      if (!jobsResult.ok) {
        console.error('Careers job board:', jobsResult.error);
        this.showMessage(this.config.labels.error);
        return;
      }

      this.jobs = jobsResult.jobs;
      this.allDepartments = departmentsResult.departments;

      if (!this.jobs.length) {
        this.filteredJobs = [];
        this.showMessage(this.config.labels.empty);
        this.updateResultCount();
        return;
      }

      this.buildFilters();
      this.applyFilters();
    }

    async fetchJobs() {
      try {
        const response = await fetch(this.config.apiUrl, {
          method: 'GET',
          headers: {
            Accept: 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Jobs API returned HTTP ${response.status}`);
        }

        const data = await response.json();

        if (!data || !Array.isArray(data.jobs)) {
          throw new Error('Jobs API response does not contain a jobs array.');
        }

        return {
          ok: true,
          jobs: data.jobs.filter((job) => job.isListed !== false)
        };
      } catch (error) {
        return { ok: false, error };
      }
    }

    async fetchDepartments() {
      // Optional: without this configured, department options come only from
      // whatever departments appear on listed jobs (today's behavior).
      if (!this.config.departmentsApiUrl) {
        return { departments: [] };
      }

      try {
        const response = await fetch(this.config.departmentsApiUrl, {
          method: 'GET',
          headers: {
            Accept: 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Departments API returned HTTP ${response.status}`);
        }

        const data = await response.json();

        // Accepts either a bare array or `{ departments: [...] }`, and either
        // plain strings or objects with a `name` field, since the exact shape
        // of the eventual proxy endpoint isn't finalized yet.
        const list = Array.isArray(data) ? data : data?.departments;

        if (!Array.isArray(list)) {
          throw new Error('Departments API response is not a recognizable list.');
        }

        const names = list
          .map((department) =>
            typeof department === 'string' ? department : department?.name
          )
          .filter((name) => Boolean(name));

        return { departments: names };
      } catch (error) {
        // Non-critical: fall back to departments derived from listed jobs.
        console.error(
          'Careers job board: departments fetch failed, falling back to job-derived departments.',
          error
        );
        return { departments: [] };
      }
    }

    buildFilters() {
      const filters = [];

      if (this.config.showDepartmentFilter) {
        // Union of departments that have listed jobs and (if configured) the
        // full Ashby department list, so departments with zero open roles
        // can still show up as a selectable filter option.
        const departmentValues = new Set([
          ...this.allDepartments,
          ...this.uniqueValues(this.jobs, 'department')
        ]);

        filters.push({
          key: 'department',
          label: this.config.labels.allDepartments,
          values: [...departmentValues].sort((a, b) => this.compareText(a, b))
        });
      }

      if (this.config.showLocationFilter) {
        filters.push({
          key: 'location',
          label: this.config.labels.allLocations,
          values: this.uniqueValues(this.jobs, 'location')
        });
      }

      if (this.config.showWorkplaceFilter) {
        filters.push({
          key: 'workplaceType',
          label: this.config.labels.allWorkplaces,
          values: this.uniqueValues(this.jobs, 'workplaceType')
        });
      }

      if (this.config.showEmploymentFilter) {
        filters.push({
          key: 'employmentType',
          label: this.config.labels.allEmployment,
          values: this.uniqueValues(this.jobs, 'employmentType')
        });
      }

      if (!filters.length) {
        this.filtersContainer.hidden = true;
        return;
      }

      this.filtersContainer.innerHTML = '';

      filters.forEach((filter) => {
        if (!filter.values.length) return;

        const wrapper = document.createElement('div');
        wrapper.className = 'careers-job-board__filter';

        const select = document.createElement('select');
        select.className = this.config.bodyStyle;
        select.setAttribute('aria-label', filter.label);
        select.dataset.filter = filter.key;

        const allOption = document.createElement('option');
        allOption.value = '';
        allOption.textContent = filter.label;
        select.appendChild(allOption);

        filter.values.forEach((value) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = this.formatValue(value);
          select.appendChild(option);
        });

        select.addEventListener('change', (event) => {
          this.filters[filter.key] = event.target.value;
          this.currentPage = 1;
          this.applyFilters();
        });

        wrapper.appendChild(select);
        this.filtersContainer.appendChild(wrapper);
      });

      this.filtersContainer.hidden =
        this.filtersContainer.children.length === 0;
    }

    uniqueValues(jobs, key) {
      return [
        ...new Set(
          jobs
            .map((job) => job[key])
            .filter((value) => Boolean(value))
        )
      ].sort((a, b) => this.compareText(a, b));
    }

    applyFilters() {
      this.filteredJobs = this.jobs.filter((job) => {
        return (
          (!this.filters.department ||
            job.department === this.filters.department) &&

          (!this.filters.location ||
            job.location === this.filters.location) &&

          (!this.filters.workplaceType ||
            job.workplaceType === this.filters.workplaceType) &&

          (!this.filters.employmentType ||
            job.employmentType === this.filters.employmentType)
        );
      });

      this.render();
    }

    render() {
      this.clearMessage();
      this.updateResultCount();

      if (!this.filteredJobs.length) {
        this.jobsContainer.innerHTML = '';
        this.paginationContainer.hidden = true;
        this.showMessage(this.getEmptyResultsMessage());
        return;
      }

      const totalPages = Math.ceil(
        this.filteredJobs.length / this.config.jobsPerPage
      );

      if (this.currentPage > totalPages) {
        this.currentPage = totalPages;
      }

      const start =
        (this.currentPage - 1) * this.config.jobsPerPage;

      const end =
        start + this.config.jobsPerPage;

      const jobsForPage =
        this.filteredJobs.slice(start, end);

      this.jobsContainer.innerHTML = '';

      const departments = this.groupByDepartment(jobsForPage);

      departments.forEach((department) => {
        this.jobsContainer.appendChild(
          this.createDepartmentGroup(
            department.name,
            department.jobs
          )
        );
      });

      this.renderPagination(totalPages);
    }

    getEmptyResultsMessage() {
      if (!this.jobs.length) {
        return this.config.labels.empty;
      }

      const onlyDepartmentFilterActive =
        Boolean(this.filters.department) &&
        !this.filters.location &&
        !this.filters.workplaceType &&
        !this.filters.employmentType;

      // A department selected on its own (no other filters stacked on top)
      // that comes back empty gets its own message instead of the generic
      // "no results" copy. Exact wording/behavior here is still pending a
      // design decision from the client (flagged as TBD) — this is a
      // reasonable default, not a final answer.
      if (onlyDepartmentFilterActive) {
        return this.config.labels.emptyDepartment;
      }

      return this.config.labels.noResults;
    }

    groupByDepartment(jobs) {
      const groups = new Map();

      jobs.forEach((job) => {
        const department =
          String(job.department || '').trim() || 'Other';

        if (!groups.has(department)) {
          groups.set(department, []);
        }

        groups.get(department).push(job);
      });

      const departments =
        Array.from(groups.entries()).map(([name, departmentJobs]) => ({
          name,
          jobs: this.sortPositions(departmentJobs)
        }));

      departments.sort((a, b) =>
        this.compareText(a.name, b.name)
      );

      return departments;
    }

    sortPositions(jobs) {
      const sorted = [...jobs];

      switch (this.config.positionSort) {
        case 'title-asc':
          return sorted.sort((a, b) =>
            this.compareText(a.title, b.title)
          );

        case 'title-desc':
          return sorted.sort((a, b) =>
            this.compareText(b.title, a.title)
          );

        case 'newest':
          return sorted.sort((a, b) =>
            new Date(b.publishedAt || 0) -
            new Date(a.publishedAt || 0)
          );

        case 'ashby':
        default:
          return sorted;
      }
    }

    createDepartmentGroup(name, jobs) {
      const section = document.createElement('section');
      section.className = 'careers-job-board__department';

      const heading = document.createElement('h3');

      heading.className = [
        'careers-job-board__department-heading',
        this.config.departmentStyle
      ]
        .filter(Boolean)
        .join(' ');

      heading.textContent = name;

      section.appendChild(heading);

      const list = document.createElement('div');
      list.className = 'careers-job-board__department-jobs';

      jobs.forEach((job) => {
        list.appendChild(this.createJobRow(job));
      });

      section.appendChild(list);

      return section;
    }

    createJobRow(job) {
      const link = document.createElement('a');

      link.className = 'careers-job-board__job';
      link.href = job.jobUrl || job.applyUrl || '#';
      link.target = this.config.linkTarget;

      if (this.config.linkTarget === '_blank') {
        link.rel = 'noopener noreferrer';
      }

      const content = document.createElement('div');
      content.className = 'careers-job-board__job-content';

      const title = document.createElement('h4');

      title.className = [
        'careers-job-board__job-title',
        this.config.positionStyle
      ]
        .filter(Boolean)
        .join(' ');

      title.textContent = job.title || '';

      content.appendChild(title);

      const metadata = [];

      if (this.config.showLocation && job.location) {
        metadata.push(job.location);
      }

      if (this.config.showWorkplace && job.workplaceType) {
        metadata.push(
          this.formatValue(job.workplaceType)
        );
      }

      if (this.config.showEmployment && job.employmentType) {
        metadata.push(
          this.formatValue(job.employmentType)
        );
      }

      if (
        this.config.showCompensation &&
        job.compensation?.compensationTierSummary
      ) {
        metadata.push(
          job.compensation.compensationTierSummary
        );
      }

      if (metadata.length) {
        const meta = document.createElement('p');

        meta.className = [
          'careers-job-board__meta',
          this.config.bodyStyle
        ]
          .filter(Boolean)
          .join(' ');

        metadata.forEach((value) => {
          const item = document.createElement('span');

          item.className =
            'careers-job-board__meta-item';

          item.textContent = value;

          meta.appendChild(item);
        });

        content.appendChild(meta);
      }

      if (
        this.config.showDescription &&
        job.descriptionPlain
      ) {
        const description = document.createElement('p');

        description.className = [
          'careers-job-board__description',
          this.config.bodyStyle
        ]
          .filter(Boolean)
          .join(' ');

        description.textContent =
          this.truncate(
            job.descriptionPlain,
            this.config.descriptionLength
          );

        content.appendChild(description);
      }

      const arrow = document.createElement('span');

      arrow.className = [
        'careers-job-board__arrow',
        this.config.bodyStyle
      ]
        .filter(Boolean)
        .join(' ');

      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '→';

      link.appendChild(content);
      link.appendChild(arrow);

      return link;
    }

    renderPagination(totalPages) {
      this.paginationContainer.innerHTML = '';

      if (totalPages <= 1) {
        this.paginationContainer.hidden = true;
        return;
      }

      const previous = this.createPageButton(
        this.config.labels.previous,
        this.currentPage - 1,
        this.currentPage === 1
      );

      this.paginationContainer.appendChild(previous);

      for (let page = 1; page <= totalPages; page += 1) {
        const button = this.createPageButton(
          String(page),
          page,
          false
        );

        if (page === this.currentPage) {
          button.setAttribute('aria-current', 'page');
        }

        this.paginationContainer.appendChild(button);
      }

      const next = this.createPageButton(
        this.config.labels.next,
        this.currentPage + 1,
        this.currentPage === totalPages
      );

      this.paginationContainer.appendChild(next);
      this.paginationContainer.hidden = false;
    }

    createPageButton(label, page, disabled) {
      const button = document.createElement('button');

      button.type = 'button';

      button.className = [
        'careers-job-board__page-button',
        this.config.bodyStyle
      ]
        .filter(Boolean)
        .join(' ');

      button.textContent = label;
      button.disabled = disabled;

      if (!disabled) {
        button.addEventListener('click', () => {
          this.currentPage = page;
          this.render();

          this.element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        });
      }

      return button;
    }

    updateResultCount() {
      if (
        !this.config.showResultCount ||
        !this.resultCountContainer
      ) {
        return;
      }

      const count = this.filteredJobs.length;

      this.resultCountContainer.textContent =
        `${count} ${count === 1 ? 'position' : 'positions'}`;
    }

    compareText(a, b) {
      return String(a || '').localeCompare(
        String(b || ''),
        undefined,
        {
          sensitivity: 'base'
        }
      );
    }

    truncate(value, maximumLength) {
      const text = String(value || '').trim();

      if (text.length <= maximumLength) {
        return text;
      }

      return `${text.slice(0, maximumLength).trim()}…`;
    }

    formatValue(value) {
      const map = {
        FullTime: 'Full-time',
        PartTime: 'Part-time',
        Intern: 'Internship',
        Contract: 'Contract',
        Temporary: 'Temporary',
        OnSite: 'On-site',
        Remote: 'Remote',
        Hybrid: 'Hybrid'
      };

      return map[value] || value;
    }

    showMessage(message) {
      if (this.messageContainer) {
        this.messageContainer.textContent = message;
      }
    }

    clearMessage() {
      if (this.messageContainer) {
        this.messageContainer.textContent = '';
      }
    }
  }

  function initializeCareersBoards(root = document) {
    root
      .querySelectorAll('[data-careers-job-board]')
      .forEach((element) => {
        if (element.dataset.careersInitialized === 'true') {
          return;
        }

        element.dataset.careersInitialized = 'true';

        new CareersJobBoard(element);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeCareersBoards();
    });
  } else {
    initializeCareersBoards();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeCareersBoards(event.target);
    }
  );
})();